<template>
	<div>
		<a-crm-container class='a-crm-payment-page'>
			<template>
				<div class='a-crm-content'>
					<div class="a-crm-payment-preloader" v-if='initLoading'>
						<div>
							<div class='a-crm-payment-preloader-icon'>
								<span v-html="require(`!html-loader!@/assets/baggage.svg`)"/>
							</div>
						</div>
					</div>
					<div class='a-crm-content-box a-crm-payment-box' v-else-if='loadError'>
						<p class='a-crm-payment-load-error'>{{ loadError }}</p>
						<v-button xxsmall outline color='green-persian' @click='retryLoad'>
							{{ $t("crm.payment.refresh") }}
						</v-button>
					</div>

					<template v-else-if='order'>
						<div class='a-crm-payment-shell' :class='{"is-paid": balance <= 0}'>
							<aside class='a-crm-payment-methods' v-if='balance > 0'>
								<p class='a-crm-payment-methods-title'>{{ $t("crm.payment.choose_method") }}</p>
								<p class='a-crm-payment-methods-order'>{{ $t("crm.payment.order_number", {number: order.crmOrderId || order.crmV2OrderId}) }}</p>

								<div class='a-crm-payment-methods-list' v-if='redirectMethods.length'>
									<button
										type='button'
										class='a-crm-payment-method'
										:class='{"active": method && method.code === item.code}'
										v-for='item in redirectMethods'
										:key='item.code'
										@click='selectMethod(item)'
									>
										<span class='a-crm-payment-method-radio'><i v-if='method && method.code === item.code'></i></span>
										<span class='a-crm-payment-method-copy'>
											<span class='a-crm-payment-method-title'>{{ item.title }}</span>
											<span class='a-crm-payment-method-meta'>{{ methodMeta(item) }}</span>
										</span>
										<span class='a-crm-payment-method-brand'>
											<img v-if='item.icon' :src='item.icon' :alt='item.title' @error='hideIcon(item)'>
											<template v-else>{{ methodBrand(item) }}</template>
										</span>
									</button>
								</div>
								<p class='a-crm-payment-empty a-crm-payment-methods-empty' v-else>{{ $t("crm.payment.no_methods") }}</p>
							</aside>

							<section class='a-crm-payment-main'>
								<div class='a-crm-payment-details'>
									<div class='a-crm-payment-details-header'>
										<p class='a-crm-payment-details-title'>{{ $t("crm.payment.details_title") }}</p>
										<button type='button' class='app-link green--text a-crm-payment-back' @click='backToOrder'>
											{{ $t("crm.payment.back_to_order") }}
										</button>
									</div>

									<div class='a-crm-payment-details-body'>
										<template v-if='balance > 0'>
											<template v-if='method'>
												<span class='a-crm-payment-badge'>{{ $t("crm.payment.secure_payment") }}</span>
												<p class='a-crm-payment-description'>{{ $t("crm.payment.method_description", {method: method.title}) }}</p>

												<ul class='a-crm-payment-options' v-if='method.options && method.options.length' :class='{"is-loading": optionsLoading}'>
													<li v-for='option in method.options' :key='option.type'>
														<button
															type='button'
															class='a-crm-payment-option'
															:class='{"active": selectedOptionType === option.type}'
															@click='selectOption(option)'
														>
															<img v-if='option.logo' :src='option.logo' :alt='option.title || option.type'>
															<span>{{ option.title || option.type }}</span>
														</button>
													</li>
												</ul>

												<div class='a-crm-payment-debt'>
													<p class='a-crm-payment-debt-title'>{{ $t("crm.payment.current_debt") }}:</p>
													<p class='a-crm-payment-debt-value'>{{ accounting ? accounting.balance.value : '—' }}</p>
												</div>

												<div class='a-crm-payment-form-row'>
													<label for='crm-payment-amount' class='a-crm-payment-form-label'>{{ $t("crm.payment.amount") }}:</label>
													<div class='a-crm-payment-amount-controls'>
														<div class='a-crm-payment-amount-wrap'>
															<input
																id='crm-payment-amount'
																class='a-crm-payment-amount'
																v-model='amount'
																type='text'
																inputmode='decimal'
																@input='error = null'
															/>
															<span>{{ order.currency }}</span>
														</div>
														<div class='a-crm-payment-percents'>
															<button type='button' class='a-crm-payment-percent' v-for='p in [10, 20, 50, 100]' :key='p' @click='setPercent(p)'>{{ p }}%</button>
														</div>
													</div>
												</div>

												<div class='a-crm-payment-converted' v-if='convertedValue'>
													<span>{{ $t("crm.payment.charge_in", {currency: method.currency}) }}:</span>
													<b>{{ convertedValue }}</b>
												</div>

												<template v-if='method.requires_payer'>
													<p class='a-crm-payment-payer-title'>{{ $t("crm.payment.payer") }}</p>
													<div class="row a-crm-payment-payer">
														<div class="col-12 col-sm-6">
															<v-text-field :label='$t("crm.payment.last_name")' name='last_name' v-model='payer.last_name' hide-details />
														</div>
														<div class="col-12 col-sm-6">
															<v-text-field :label='$t("crm.payment.first_name")' name='first_name' v-model='payer.first_name' hide-details />
														</div>
														<div class="col-12 col-sm-6">
															<v-text-field :label='$t("crm.payment.email")' name='email' type='email' v-model='payer.email' hide-details />
														</div>
														<div class="col-12 col-sm-6">
															<v-text-field :label='$t("crm.payment.phone")' name='phone' v-model='payer.phone' hide-details />
														</div>
													</div>
												</template>

												<v-checkbox name='agree' v-model='agree' :label='$t("crm.payment.agree")' class='a-crm-payment-agree' />
												<p class='a-crm-payment-error' v-if='error'>{{ error }}</p>
											</template>

											<p class='a-crm-payment-empty' v-else>{{ $t("crm.payment.no_methods") }}</p>
										</template>

										<div class='a-crm-payment-paid' v-else>
											<p class='a-crm-payment-paid-title'>{{ $t("crm.payment.paid") }}</p>
											<p class='a-crm-payment-paid-overpayment' v-if='accounting && accounting.overpayment.amount > 0'>
												{{ $t("crm.payment.overpayment") }}: {{ accounting.overpayment.value }}
											</p>
										</div>
									</div>
								</div>

								<div class='a-crm-payment-footer' v-if='balance > 0 && method'>
									<p>{{ $t("crm.payment.total_due") }}: <b>{{ paymentTotal }}</b></p>
									<v-button
										xxsmall
										color='green-persian white--text'
										class='a-crm-payment-submit'
										:loading='paying'
										:disabled='!canPay'
										@click='pay'
									>{{ $t("crm.payment.pay") }}</v-button>
								</div>
							</section>
						</div>

						<div class='a-crm-content-box a-crm-payment-history'>
							<div class='a-crm-payment-header'>
								<p class='a-crm-payment-section-title'>{{ $t("crm.payment.payments") }}</p>
								<v-button xxsmall outline color='green-persian' :loading='refreshing' @click='refresh'>
									{{ $t("crm.payment.refresh") }}
								</v-button>
							</div>

							<template v-if='payments.length'>
								<div class='a-crm-payment-item' v-for='p in payments' :key='p.id'>
									<div class='a-crm-payment-item-info'>
										<p class='a-crm-payment-item-name'>
											{{ p.category ? p.category.name : p.payment_method }}
											<span class='a-crm-payment-item-refund' v-if='p.type === "expense"'>· {{ $t("crm.payment.refund") }}</span>
										</p>
										<p class='a-crm-payment-item-date'>{{ p.created_at }} · {{ p.payment_method }}</p>
									</div>
									<div class='a-crm-payment-item-right'>
										<p class='a-crm-payment-item-amount' :class='{"is-expense": p.type === "expense"}'>
											{{ p.type === "expense" ? "−" : "" }}{{ p.amount }} {{ p.currency }}
										</p>
										<span class='a-crm-payment-item-status' :class='p.status'>
											{{ $t(`crm.payment.status.${p.status}`) }}
										</span>
									</div>
								</div>
							</template>
							<p class='a-crm-payment-empty' v-else>{{ $t("crm.payment.no_payments") }}</p>
						</div>
					</template>
				</div>
			</template>
		</a-crm-container>
	</div>
</template>

<script>
import ACrmContainer from '@/views/crm/container';
import {cabinet} from '@/services/request';
import {amountByPercent, convertAmount, parsePaymentAmount} from '@/views/crm/payment-utils.mjs';

export default {
	components: {
		ACrmContainer
	},
	data: () => ({
		order: null,
		methods: [],
		payments: [],
		selectedCode: null,
		selectedOptionType: null,
		optionsLoading: false,
		optionsTimer: null,
		amount: '',
		payer: {first_name: '', last_name: '', email: '', phone: ''},
		agree: false,
		error: null,
		loadError: null,
		initLoading: true,
		refreshing: false,
		paying: false,
		retry: null
	}),
	computed: {
		accounting() {
			return this.order && this.order.accounting;
		},
		balance() {
			return this.accounting ? this.accounting.balance.amount : 0;
		},
		redirectMethods() {
			// kind invoice появится на бэке позже — пока не рисуем
			return this.methods.filter(m => m.kind === 'redirect');
		},
		method() {
			return this.redirectMethods.find(m => m.code === this.selectedCode) || null;
		},
		parsedAmount() {
			return parsePaymentAmount(this.amount);
		},
		convertedValue() {
			if (!this.method || !this.order || !this.parsedAmount || this.method.currency === this.order.currency) return null;
			const amount = convertAmount(this.parsedAmount, this.method.exchange_rate).toFixed(2);
			return `${this.$helpers.numberWithSpaces(amount)} ${this.method.currency}`;
		},
		canPay() {
			return Boolean(this.agree && this.method && Number.isFinite(this.parsedAmount) && this.parsedAmount > 0 && this.parsedAmount <= this.balance);
		},
		paymentTotal() {
			if (!this.order || !Number.isFinite(this.parsedAmount) || this.parsedAmount <= 0) return '—';
			const amount = this.$helpers.numberWithSpaces(this.parsedAmount.toFixed(2));
			return `${amount} ${this.$helpers.isoCurrencies(this.order.currency)}`;
		}
	},
	created() {
		this.getData().then(() => {
			this.initLoading = false;

			// возврат со шлюза: колбэк мог ещё не дойти — перепрашиваем пару раз
			if (this.$route.query.paid) {
				this.$router.replace({query: {}});
				this.retry = setTimeout(() => {
					this.refresh();
					this.retry = setTimeout(() => this.refresh(), 2500);
				}, 2500);
			}
		}).catch(err => this.handleLoadError(err));
	},
	beforeDestroy() {
		clearTimeout(this.retry);
	},
	watch: {
		// Сумму меняют и кнопки процентов, и ручной ввод — следим за значением,
		// а не за каждым обработчиком.
		amount() {
			this.reloadOptions();
		}
	},
	methods: {
		getData() {
			this.loadError = null;
			const id = this.$route.params.id;
			return Promise.all([
				cabinet.orderV2.get(id),
				cabinet.orderV2.paymentMethods(id),
				cabinet.orderV2.payments(id)
			]).then(([order, methods, payments]) => {
				this.order = order.data;
				this.methods = methods.data;
				this.payments = payments.data;
				if (!this.method && this.redirectMethods.length) {
					this.selectedCode = this.redirectMethods[0].code;
				}
				this.syncAmountWithBalance(true);
			});
		},
		refresh() {
			this.refreshing = true;
			const id = this.$route.params.id;
			return Promise.all([cabinet.orderV2.get(id), cabinet.orderV2.payments(id)]).then(([order, payments]) => {
				this.order = order.data;
				this.payments = payments.data;
				this.syncAmountWithBalance();
			}).catch(err => {
				this.error = this.errorMessage(err) || this.$t('crm.payment.load_error');
			}).finally(() => this.refreshing = false);
		},
		retryLoad() {
			this.initLoading = true;
			this.getData().then(() => {
				this.initLoading = false;
			}).catch(err => this.handleLoadError(err));
		},
		handleLoadError(err) {
			this.loadError = this.errorMessage(err) || this.$t('crm.payment.load_error');
			this.initLoading = false;
		},
		errorMessage(err) {
			return err && err.response && err.response.data && err.response.data.message;
		},
		hideIcon(item) {
			// Логотип не загрузился — откатываемся на текстовое название,
			// пустое место в списке хуже, чем надпись.
			this.$set(item, 'icon', null);
		},
		methodBrand(item) {
			if (item.code === 'liqpay') return 'LiqPay';
			if (item.code === 'euro_acquiring') return 'EUR';
			return item.code.replace(/_/g, ' ').slice(0, 12);
		},
		methodMeta(item) {
			if (!this.order || item.currency === this.order.currency) {
				return this.$t('crm.payment.payment_in', {currency: item.currency});
			}
			return `1 ${this.order.currency} = ${this.$helpers.numberWithSpaces(item.exchange_rate)} ${item.currency}`;
		},
		syncAmountWithBalance(force = false) {
			if (this.balance <= 0) {
				this.amount = '';
				return;
			}
			if (force || !Number.isFinite(this.parsedAmount) || this.parsedAmount <= 0 || this.parsedAmount > this.balance) {
				this.amount = String(amountByPercent(this.balance, 100));
			}
		},
		selectOption(option) {
			// Повторный клик снимает выбор: без него агент не может вернуться
			// к общему экрану Antom, где методов больше.
			this.selectedOptionType = this.selectedOptionType === option.type ? null : option.type;
		},
		// Набор способов зависит от суммы: у методов есть пороги. Перезапрашиваем
		// с задержкой — кнопки процентов и ручной ввод иначе дали бы поток запросов.
		reloadOptions() {
			if (this.initLoading) return;

			clearTimeout(this.optionsTimer);

			this.optionsTimer = setTimeout(() => {
				const item = this.method;
				const amount = this.parsedAmount;

				if (!item || !item.options || !amount) return;

				this.optionsLoading = true;
				cabinet.orderV2.paymentOptions(this.$route.params.id, item.code, amount)
					.then(r => {
						this.$set(item, 'options', r.data);
						// Выбранный способ мог пропасть из набора под новую сумму —
						// молча слать его в оплату нельзя, шлюз ответит ошибкой.
						if (this.selectedOptionType && !r.data.some(o => o.type === this.selectedOptionType)) {
							this.selectedOptionType = null;
						}
					})
					.finally(() => this.optionsLoading = false);
			}, 400);
		},
		selectMethod(item) {
			this.selectedOptionType = null;
			this.selectedCode = item.code;
			this.error = null;
		},
		setPercent(percent) {
			this.amount = String(amountByPercent(this.balance, percent));
			this.error = null;
		},
		backToOrder() {
			this.$router.push({
				name: 'my-order',
				params: {id: this.$route.params.id, lang: this.$route.params.lang},
				query: {crm: 'v2'}
			});
		},
		pay() {
			if (!this.method || !Number.isFinite(this.parsedAmount) || this.parsedAmount <= 0 || this.parsedAmount > this.balance) {
				this.error = this.$t('crm.payment.amount_error');
				return;
			}

			this.error = null;
			this.paying = true;

			const returnUrl = window.location.origin + this.$router.resolve({
				name: 'my-order-payment',
				params: {id: this.$route.params.id, lang: this.$route.params.lang},
				query: {paid: '1'}
			}).href;

			cabinet.orderV2.pay(this.$route.params.id, this.method.code, {
				amount: this.parsedAmount,
				return_url: returnUrl,
				...(this.selectedOptionType ? {payment_method_type: this.selectedOptionType} : {}),
				...(this.method.requires_payer ? {payer: this.payer} : {})
			}).then(r => {
				if (r.data.payment_url) {
					window.location.href = r.data.payment_url;
				} else {
					this.submitGatewayForm(r.data);
				}
			}).catch(err => {
				// 422 формирует CRM — показываем как есть
				this.error = this.errorMessage(err) || this.$t('crm.payment.gateway_error');
				this.paying = false;
			});
		},
		submitGatewayForm({action_url, data, signature}) {
			if (!action_url || !data || !signature) {
				throw new Error('Invalid payment gateway response');
			}
			const form = document.createElement('form');
			form.method = 'POST';
			form.action = action_url;
			form.acceptCharset = 'utf-8';

			for (const [name, value] of [['data', data], ['signature', signature]]) {
				const input = document.createElement('input');
				input.type = 'hidden';
				input.name = name;
				input.value = value;
				form.appendChild(input);
			}

			document.body.appendChild(form);
			form.submit();
		}
	}
}
</script>

<style lang="scss">
@import "@/styles/global/variables";
@import "@/styles/global/mixins";

.a-crm-payment {
	&-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		margin-bottom: 20px;
	}

	&-error {
		font-size: 13px;
		color: $red;
		margin-bottom: 15px;
	}

	&-paid {
		text-align: center;
		padding: 20px 0;

		&-title {
			font-size: 20px;
			font-weight: 700;
			color: $green-persian;
		}

		&-overpayment {
			font-size: 14px;
			margin-top: 10px;
		}
	}

	&-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
		padding: 16px 18px;
		background-color: $white;
		border: 1px solid rgba($black, .08);
		border-radius: $border-radius * 2;
		transition: border-color $transition;
		@include maxw($grid-breakpoints-xs - 1) {
			align-items: flex-start;
			flex-direction: column;
		}

		&:not(:last-child) {
			margin-bottom: 10px;
		}

		&:hover {
			border-color: rgba($green-persian, .35);
		}

		&-info {
			min-width: 0;
		}

		&-name {
			font-size: 15px;
			font-weight: 700;
		}

		&-refund {
			color: $red;
			font-weight: 400;
		}

		&-date {
			font-size: 12px;
			color: rgba($black, .5);
			margin-top: 3px;
		}

		&-right {
			display: flex;
			align-items: center;
			gap: 16px;
			text-align: right;
			@include maxw($grid-breakpoints-xs - 1) {
				justify-content: space-between;
				width: 100%;
				text-align: left;
			}
		}

		&-amount {
			font-size: 15px;
			font-weight: 700;
			color: $green-persian;

			&.is-expense {
				color: $red;
			}
		}

		&-status {
			display: inline-block;
			font-size: 11px;
			font-weight: 700;
			border-radius: 999px;
			padding: 5px 10px;
			color: $cyprus;
			background-color: $cyan-light;

			&.pending {
				color: $orange;
				background-color: rgba($orange, .12);
			}
		}
	}

	&-empty {
		font-size: 14px;
		color: rgba($black, .5);
	}

	&-load-error {
		font-size: 14px;
		color: $red;
		margin-bottom: 15px;
	}

	&-preloader {
		padding: 50px 0;
		display: flex;
		align-items: center;
		justify-content: center;

		&-icon {
			width: 125px;
			height: 132px;
		}
	}
}

.a-crm-payment-page {
	.a-crm-container {
		max-width: 1480px;
	}

	.a-crm-wrap {
		padding-top: 30px;
	}
}

.a-crm-payment {
	&-shell {
		display: grid;
		grid-template-columns: minmax(300px, 35%) minmax(0, 65%);
		min-height: 690px;
		background-color: $azure;
		border-radius: $border-radius * 2;
		overflow: hidden;

		&.is-paid {
			grid-template-columns: 1fr;
			min-height: 360px;
		}
	}

	&-methods {
		padding: 44px 28px;
		background-color: $cyan-light;

		&-title {
			font-size: 22px;
			font-weight: 700;
			line-height: 1.3;
			color: $cyprus;
		}

		&-order {
			font-size: 13px;
			color: rgba($cyprus, .55);
			margin-top: 5px;
		}

		&-list,
		&-empty {
			margin-top: 28px;
		}
	}

	&-method {
		display: flex;
		align-items: center;
		width: 100%;
		min-height: 82px;
		font-family: inherit;
		text-align: left;
		background-color: $white;
		border: 2px solid rgba($black, .1);
		border-radius: $border-radius * 2;
		padding: 14px 18px;
		cursor: pointer;
		transition: border-color $transition, box-shadow $transition, transform $transition;

		&:not(:last-child) {
			margin-bottom: 16px;
		}

		&:hover {
			transform: translateY(-1px);
			border-color: rgba($green-persian, .45);
		}

		&.active {
			border-color: $green-persian;
			box-shadow: 0 2px 6px rgba($green-persian, .12);
		}

		&-radio {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 20px;
			height: 20px;
			border: 1px solid rgba($black, .2);
			border-radius: 50%;
			margin-right: 16px;

			i {
				width: 10px;
				height: 10px;
				border-radius: 50%;
				background-color: $green-persian;
			}
		}

		&-copy {
			display: flex;
			flex: 1;
			min-width: 0;
			flex-direction: column;
		}

		&-title {
			font-size: 15px;
			font-weight: 700;
			line-height: 1.25;
			color: $black;
		}

		&-meta {
			font-size: 12px;
			line-height: 1.35;
			color: rgba($black, .5);
			margin-top: 7px;
		}

		&-brand {
			flex-shrink: 0;
			max-width: 86px;

			img {
				display: block;
				max-width: 86px;
				max-height: 24px;
				width: auto;
				height: auto;
				margin-left: auto;
			}

			font-size: 14px;
			font-weight: 800;
			line-height: 1;
			text-align: right;
			text-transform: uppercase;
			color: $green-persian;
			margin-left: 12px;
		}
	}

	&-main {
		display: flex;
		min-width: 0;
		flex-direction: column;
		padding: 32px 34px 28px;
	}

	&-details {
		background-color: $white;
		box-shadow: 0 0 10px rgba(86, 86, 86, .1);
		border-radius: $border-radius * 2;
		overflow: hidden;

		&-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 20px;
			padding: 26px 30px;
			border-bottom: 1px solid rgba($black, .1);
		}

		&-title {
			font-size: 19px;
			font-weight: 700;
			line-height: 1.3;
			color: $black;
		}

		&-body {
			padding: 30px;
		}
	}

	&-back {
		flex-shrink: 0;
		font-family: inherit;
		font-size: 12px;
		font-weight: 600;
	}

	&-section-title {
		font-size: 19px;
		font-weight: 700;
	}

	&-debt {
		display: grid;
		grid-template-columns: minmax(220px, 42%) 1fr;
		align-items: center;
		gap: 24px;
		padding: 0;
		background-color: transparent;
		border-radius: 0;
		margin-bottom: 24px;

		&-title {
			font-size: 15px;
			font-weight: 700;
		}

		&-value {
			font-size: 17px;
			font-weight: 700;
		}
	}

	&-options {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		list-style: none;
		margin: 0 0 20px;
		padding: 0;
	}

	&-options.is-loading {
		opacity: .5;
		pointer-events: none;
	}

	&-option {
		display: flex;
		width: 100%;
		background: none;
		cursor: pointer;
		align-items: center;
		gap: 8px;
		padding: 6px 10px;
		border: 1px solid rgba($cyprus, .12);
		border-radius: 8px;
		font-size: 13px;

		img {
			display: block;
			width: 28px;
			height: 20px;
			object-fit: contain;
		}

		&.active {
			border-color: $green-persian;
			box-shadow: inset 0 0 0 1px $green-persian;
		}
	}

	&-badge {
		display: inline-block;
		font-size: 12px;
		font-weight: 700;
		line-height: 1;
		color: $cyprus;
		background-color: $cyan-light;
		border-radius: $border-radius;
		padding: 6px 9px;
		margin-bottom: 14px;
	}

	&-description {
		font-size: 14px;
		line-height: 1.5;
		color: rgba($black, .72);
		margin-bottom: 28px;
	}

	&-form-row {
		display: grid;
		grid-template-columns: minmax(220px, 42%) 1fr;
		align-items: center;
		gap: 24px;
	}

	&-form-label {
		font-size: 15px;
		font-weight: 700;
	}

	&-amount-controls {
		display: flex;
		align-items: center;
		gap: 24px;
		min-width: 0;
	}

	&-amount-wrap {
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 130px;
		border: 1px solid rgba($black, .15);
		border-radius: $border-radius;
		transition: border-color $transition;

		&:focus-within {
			border-color: $green-persian;
		}

		span {
			flex-shrink: 0;
			font-size: 12px;
			font-weight: 700;
			color: rgba($black, .45);
			padding-right: 12px;
		}
	}

	&-amount {
		width: 100%;
		min-width: 0;
		font-family: inherit;
		font-size: 15px;
		font-weight: 500;
		color: $black;
		background: transparent;
		border: 0;
		border-radius: 0;
		padding: 13px 12px;
		outline: none;
	}

	&-percents {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		margin-top: 0;
	}

	&-percent {
		min-width: 60px;
		height: 44px;
		font-family: inherit;
		font-size: 14px;
		font-weight: 700;
		color: $green-persian;
		background-color: $white;
		border: 1px solid $green-persian;
		border-radius: $border-radius;
		padding: 0 12px;
		margin: 0;
		cursor: pointer;
		transition: color $transition, background-color $transition;

		&:hover {
			color: $white;
			background-color: $green-persian;
			border-color: $green-persian;
		}
	}

	&-converted {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
		font-size: 14px;
		background-color: rgba($green-persian, .08);
		border-radius: 8px;
		padding: 12px 14px;
		margin: 18px 0 4px calc(42% + 12px);
	}

	&-payer-title {
		font-size: 16px;
		font-weight: 700;
		margin: 30px 0 16px;
	}

	&-payer {
		margin-bottom: -12px;

		> [class*="col-"] {
			padding-bottom: 12px;
		}
	}

	&-agree {
		margin-top: 22px;
	}

	&-error {
		margin: 10px 0 0;
	}

	&-footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 28px;
		margin-top: auto;
		padding: 34px 0 6px;

		p {
			font-size: 20px;
			font-weight: 600;
		}

		.a-crm-payment-submit.app-btn {
			min-width: 160px;
		}
	}

	&-paid {
		padding: 60px 0;
	}

	&-history {
		margin-top: 24px;
		background-color: $azure;
		border: 1px solid rgba($green-persian, .08);

		.a-crm-payment-header {
			padding-bottom: 18px;
			border-bottom: 1px solid rgba($black, .08);
		}

		.a-crm-payment-empty {
			padding: 18px;
			background-color: $white;
			border-radius: $border-radius * 2;
		}
	}

	&-empty {
		line-height: 1.5;
	}
}

@media (max-width: 1199px) {
	.a-crm-payment {
		&-shell {
			grid-template-columns: 310px minmax(0, 1fr);
		}

		&-methods {
			padding: 34px 22px;
		}

		&-main {
			padding: 26px;
		}

		&-amount-controls {
			align-items: stretch;
			flex-direction: column;
			gap: 12px;
		}

		&-converted {
			margin-left: 0;
		}
	}
}

@media (max-width: 959px) {
	.a-crm-payment {
		&-shell {
			grid-template-columns: 1fr;
			min-height: 0;
		}

		&-methods-list {
			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 14px;
		}

		&-method:not(:last-child) {
			margin-bottom: 0;
		}

		&-main {
			min-height: 560px;
		}
	}
}

@media (max-width: 599px) {
	.a-crm-payment-page .a-crm-wrap {
		padding-top: 15px;
	}

	.a-crm-payment {
		&-shell {
			border-radius: $border-radius * 2;
		}

		&-methods {
			padding: 26px 16px;

			&-title {
				font-size: 20px;
			}

			&-list {
				grid-template-columns: 1fr;
				margin-top: 20px;
			}
		}

		&-main {
			min-height: 0;
			padding: 16px;
		}

		&-details {
			border-radius: $border-radius * 2;

			&-header,
			&-body {
				padding: 20px 16px;
			}

			&-header {
				align-items: flex-start;
				flex-direction: column;
				gap: 8px;
			}
		}

		&-debt,
		&-form-row {
			grid-template-columns: 1fr;
			gap: 10px;
		}

		&-percents {
			display: grid;
			grid-template-columns: repeat(4, 1fr);
		}

		&-percent {
			min-width: 0;
			padding: 0 6px;
		}

		&-converted {
			align-items: flex-start;
			flex-direction: column;
			gap: 4px;
		}

		&-footer {
			align-items: stretch;
			flex-direction: column;
			gap: 16px;

			p {
				font-size: 18px;
			}

			.a-crm-payment-submit.app-btn {
				width: 100%;
			}
		}
	}
}
</style>
